import { useEffect, useRef, useState } from 'react';

interface CheckinIntroCardProps {
  onStart: () => void;
  onSkip: () => void;
  onClose?: () => void;
}

const SLIDES = [
  {
    icon: '😤🩸😟',
    title: 'Check in with yourself',
    body: 'Stress, pain, worry and how you\'re feeling — a few taps.',
    gradient: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-hover) 100%)',
    dotColor: 'var(--color-accent)',
  },
  {
    icon: '🗺️🧠💪',
    title: 'Map what\'s happening',
    body: 'Body map, emotion map, and what\'s helping you cope.',
    gradient: 'linear-gradient(135deg, var(--color-secondary) 0%, #d08898 100%)',
    dotColor: 'var(--color-secondary)',
  },
  {
    icon: '📊✨',
    title: 'Build your health story',
    body: 'Each check-in feeds your Patterns and earns XP toward your next level.',
    gradient: 'linear-gradient(135deg, var(--color-warning) 0%, #dab050 100%)',
    dotColor: 'var(--color-warning)',
  },
];

export default function CheckinIntroCard({ onStart, onSkip, onClose }: CheckinIntroCardProps) {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const slide = SLIDES[current];
  const isLast = current === SLIDES.length - 1;

  function advance() {
    if (animating) return;
    if (isLast) {
      onStart();
      return;
    }
    setAnimating(true);
    timerRef.current = setTimeout(() => {
      setCurrent(c => c + 1);
      setAnimating(false);
    }, 180);
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.96)',
        zIndex: 9500,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'checkinIntroFadeIn 0.4s ease',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Check-in intro"
    >
      <div
        style={{
          width: '100%',
          maxWidth: 380,
          background: 'var(--color-bg-card, #1a2035)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
          boxShadow: 'var(--shadow-overlay)',
          overflow: 'hidden',
          animation: 'checkinIntroPop 0.45s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          position: 'relative',
        }}
      >
        {onClose && (
          // eslint-disable-next-line cw/no-inline-button-style -- CheckinIntro modal close × — 32px round icon button, not a chamfered .btn variant
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              background: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
              color: '#fff',
              fontSize: 'var(--fs-md)',
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >×</button>
        )}

        <div
          style={{
            background: slide.gradient,
            padding: '32px 24px 28px',
            textAlign: 'center',
            transition: 'background 0.3s ease',
          }}
        >
          <div
            style={{
              fontSize: 'var(--fs-4xl-plus)',
              lineHeight: 1,
              marginBottom: 14,
              opacity: animating ? 0 : 1,
              transform: animating ? 'translateY(8px)' : 'translateY(0)',
              transition: 'opacity 0.18s ease, transform 0.18s ease',
            }}
          >
            {slide.icon}
          </div>
          <h2
            style={{
              fontSize: 'var(--fs-lg)',
              fontWeight: 800,
              color: '#fff',
              margin: '0 0 8px',
              opacity: animating ? 0 : 1,
              transition: 'opacity 0.18s ease',
            }}
          >
            {slide.title}
          </h2>
          <p
            style={{
              fontSize: 'var(--fs-sm)',
              color: 'rgba(255,255,255,0.88)',
              margin: 0,
              lineHeight: 1.55,
              opacity: animating ? 0 : 1,
              transition: 'opacity 0.18s ease',
            }}
          >
            {slide.body}
          </p>
        </div>

        <div style={{ padding: '20px 20px 24px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 20,
            }}
          >
            {SLIDES.map((s, i) => (
              <div
                key={i}
                style={{
                  width: i === current ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === current ? slide.dotColor : 'var(--color-accent-light)',
                  transition: 'width 0.3s ease, background 0.3s ease',
                }}
              />
            ))}
          </div>

          {/* eslint-disable-next-line cw/no-inline-button-style -- CheckinIntro Next/Let's-go — uses per-slide gradient (slide.gradient), parked onboarding gradient family */}
          <button
            onClick={advance}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: slide.gradient,
              color: '#fff',
              fontSize: 'var(--fs-sm-plus)',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: `0 4px 16px color-mix(in srgb, ${slide.dotColor} 40%, transparent)`,
              marginBottom: 10,
              transition: 'background 0.3s ease, box-shadow 0.3s ease',
            }}
          >
            {isLast ? "Let's go 🚀" : 'Next →'}
          </button>

          {/* eslint-disable-next-line cw/no-inline-button-style -- CheckinIntro Skip intro — bare text-only secondary action, not a chamfered .btn variant */}
          <button
            onClick={onSkip}
            style={{
              width: 'auto',
              padding: '4px 8px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 400,
              cursor: 'pointer',
              textDecoration: 'none',
              margin: '0 auto',
              display: 'block',
            }}
          >
            Skip intro
          </button>
        </div>
      </div>

      <style>{`
        @keyframes checkinIntroFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes checkinIntroPop {
          from { opacity: 0; transform: scale(0.9) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }
        html.reduced-motion * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
      `}</style>
    </div>
  );
}
